<template>
  <div class="docs-content">
    <div v-if="canEdit">
      <p class="text-muted">
        Pitch deck
      </p>

      <draggable
        v-model="pitchDocuments"
        element="ul"
        class="files clearfix"
        :options="{ handle: '.handle' }"
      >
        <document
          v-for="document in pitchDocuments"
          :key="document.url"
          :document="document"
          :startup-id="startup.id"
          class="file-pdf"
        />
      </draggable>

      <p class="text-muted">
        Other documents
      </p>

      <draggable
        v-model="otherDocuments"
        element="ul"
        class="files clearfix"
        :options="{ handle: '.handle' }"
      >
        <document
          v-for="document in otherDocuments"
          :key="document.url"
          :document="document"
          :startup-id="startup.id"
          hide-allow-download
          class="file-pdf"
        />
      </draggable>

      <button
        class="btn btn-primary btn-sm"
        :disabled="uploadInProgress"
        @click="uploadDocument({ isPitchDeck: true })"
      >
        Add pitch deck
      </button>
      <button
        class="btn btn-primary btn-sm"
        @click="uploadDocument({ isPitchDeck: false, isVideo: false })"
        :disabled="uploadInProgress || !pitchDocuments.length"
      >
        Add other document
      </button>
      <p v-show="!pitchDocuments.length" class="text-muted text-sm">
        {{ helpMessages.documentsOthersNoPitchYet }}
      </p>

      <transition leave-active-class="animated fadeOut">
        <div v-show="uploadInProgress" class="progress margin-top-30">
          <vb-progressbar label="File upload" :now="progress" type="success" />
        </div>
      </transition>

      <input
        ref="fileInput"
        type="file"
        class="hidden"
        @change="
          onFilesChosen({
            filename: $event.target.name,
            file: $event.target.files[0],
            event: $event,
          })
        "
      />
    </div>

    <div v-else-if="documents.length === 0">
      <p class="text-muted">
        There are no documents yet.
      </p>
    </div>

    <div v-else>
      <template v-if="pitchDocuments.length">
        <p class="text-muted">
          Pitch deck
        </p>
        <ul class="files clearfix">
          <li
            v-for="document in pitchDocuments"
            :key="document.url"
            class="file-pdf"
          >
            <span class="icon icon-file"></span>
            {{ document.filename }}
            <TextButton
              type="primary"
              size="lg"
              class="float-right font-semibold"
              @click="openDocument(document)"
            >
              Open
            </TextButton>
          </li>
        </ul>
      </template>

      <template v-if="otherDocuments.length">
        <p class="text-muted">
          Other documents
        </p>
        <ul class="files clearfix">
          <li
            v-for="document in otherDocuments"
            :key="document.url"
            class="file-pdf"
          >
            <span class="icon icon-file"></span>
            {{ document.filename }}
            <TextButton
              type="primary"
              size="lg"
              class="float-right font-semibold"
              @click="openDocument(document)"
            >
              Open
            </TextButton>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import http from 'services/http'
import _ from 'lodash'
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import mime from 'mime-types'
import TextButton from 'components/common/TextButton.vue'
import helpMessages from './helpMessages'
import Document from './Document'

export default {
  components: { Document, draggable, TextButton },
  props: {
    startup: {
      type: Object,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    documents: {
      get() {
        return this.startup.documents || []
      },
      async set(documents) {
        this.$store.commit('startupProfile/SET_STARTUP_ATTR', {
          attr: 'documents',
          value: documents,
        })
        await this.$store.dispatch('startupProfile/reorderDocuments', {
          documentsIds: this.documents.map((d) => d.id),
        })
      },
    },
    pitchDocuments: {
      get() {
        return this.documents
          .filter((d) => d.is_pitch)
          .map((document, idx) => {
            let filename = `${this.startup.name}_Deck`
            if (idx >= 1) {
              filename += `_${idx + 1}`
            }
            return {
              ...document,
              originalFilename: document.filename,
              filename,
            }
          })
      },
      async set(pitchDocuments) {
        this.documents = pitchDocuments.concat(this.otherDocuments)
      },
    },
    otherDocuments: {
      get() {
        return this.documents.filter((d) => !d.is_pitch && !d.is_video)
      },
      async set(otherDocuments) {
        this.documents = this.pitchDocuments.concat(otherDocuments)
      },
    },
    video() {
      return this.documents.filter((d) => d.is_video)
    },
    ...mapState({
      user: (state) => state.user.data,
      canEdit(state) {
        const isAdmin = state.user.data.isAdmin
        const startups = state.user.data.startups || []
        const startupIds = new Set(startups.map((s) => s.id))
        const isOwner = startupIds.has(this.startup.id)
        return this.editable && (isAdmin || isOwner)
      },
    }),
  },
  data() {
    return {
      helpMessages,
      uploadInProgress: false,
      isRemoveInProgress: false,
      progress: 0,
      goingToUploadPitch: undefined,
      goingToUploadVideo: undefined,
      isUploading: false,
    }
  },
  methods: {
    uploadDocument({ isPitchDeck = false, isVideo = false } = {}) {
      this.$refs.fileInput.click()
      this.goingToUploadPitch = isPitchDeck
      this.goingToUploadVideo = isVideo
    },
    async onFilesChosen({ filename, file, event }) {
      if (!file) {
        return
      }

      // take isPitch value and reset goingToUploadPitch state
      const isPitch = this.goingToUploadPitch
      this.goingToUploadPitch = undefined
      const isVideo = this.goingToUploadVideo
      this.goingToUploadVideo = undefined

      // Check file size
      if (file.size >= 20971520) {
        this.$message.error({
          message: 'File cannot exceed 20 MB in size!',
          duration: 5000,
        })
        return
      }

      const disallowedExtensions = ['doc', 'docx', 'ppt', 'pptx']
      const allowedVideoExtensions = ['mp4', 'mkv', 'avi', 'mov']
      const ext = mime.extension(file.type)
      if (isPitch && ext !== 'pdf') {
        this.$message.error('Please upload a PDF file')
        this.$refs.fileInput.value = null
        return
      }
      if (!isPitch && !isVideo && disallowedExtensions.includes(ext)) {
        this.$message.error(
          'Word documents and presentations are not allowed. Please upload a PDF file, image or spreadsheet',
        )
        this.$refs.fileInput.value = null
        return
      }
      if (!isPitch && isVideo && !allowedVideoExtensions.includes(ext)) {
        this.$message.error(
          `Only videos with these extensions can be uploaded: ${allowedVideoExtensions.join(
            ', ',
          )}`,
        )
        this.$refs.fileInput.value = null
        return
      }
      const self = this
      self.uploadInProgress = true

      const response = await http.postAuthed(
        `/startups/${this.startup.id}/document-upload-data`,
        {
          startup_id: this.startup.id,
          filename: file.name,
          content_type: file.type,
        },
      )
      const key = response.data.fields.key
      const documentUrl = response.data.url + key

      const data = new FormData()
      _.toPairs(response.data.fields).forEach((pair) => {
        data.append(pair[0], pair[1])
      })
      data.append('file', file)
      const uploadResponse = await axios.post(response.data.url, data, {
        onUploadProgress(progressEvent) {
          self.progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
        },
      })

      const document = {
        startup_id: self.startup.id,
        filename: file.name,
        key: key,
        url: documentUrl,
        size: file.size,
        content_type: file.type,
        is_pitch: isPitch,
        is_video: isVideo,
      }
      const notifyResponse = await http.postAuthed(
        `/startups/${this.startup.id}/document-uploaded`,
        document,
      )
      self.$store.commit('startupProfile/ADD_DOCUMENT', notifyResponse.data)

      setTimeout(() => {
        self.uploadInProgress = false
        self.progress = 0
        self.$refs.fileInput.value = null
      }, 700)
    },
    async openRemoveModal(document) {
      try {
        await this.$confirm(
          `Are you sure you want to delete document ${document.filename}?`,
          'Confirm deletion',
        )
      } catch (err) {
        // removal canceled
        return
      }
      return await this.remove({ document })
    },
    async remove({ document }) {
      this.isRemoveInProgress = true
      await this.$store.dispatch('startupProfile/removeDocument', { document })
      this.isRemoveInProgress = false
      this.$message({ type: 'success', message: 'Document removed' })
    },
    async openDocument(document) {
      // This is here to fix expiring S3 links for Pitch Deck files. They expire by default in 1 hour
      // (this only makes sense when not editing - at editor it might happen in the middle of adding a team member)
      if (this.$route.name === 'privateStartupProfile') {
        const startupId = this.$route.params.startupId
        const linkId = this.$route.params.linkId

        await this.$store.dispatch('startupProfile/fetchPrivate', {
          startupId,
          linkId,
        })
      } else {
        await this.$store.dispatch('startupProfile/fetch', {
          id: this.startup.id,
          delay: 750,
        })
      }
      // XXX: this code assumes that clicked document is not removed at the backend
      const newDocument = this.startup.documents.filter((d) => {
        return d.id === document.id
      })[0]
      window.open(newDocument.url, '_new').focus()
    },
    async onAllowDownloadChanged(document, value) {
      this.isUploading = true

      await this.$store.dispatch('startupProfile/updateDocument', {
        ...document,
        allow_download: value,
      })

      this.isUploading = false
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.docs-content {
  padding: 18px 17px;

  .files {
    .file-pdf {
      &.sortable-ghost {
        background-color: $grey160;
      }
    }
  }
}

.section-header {
  margin: 24px 0 30px;
}
</style>
